import { useState } from 'react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { InfoCard } from '@backstage/core-components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import useAsync from 'react-use/esm/useAsync';
import {
  getSurfaceColor,
  getSurfaceBorder,
  getThemeTokens,
} from '../../theme/tokens';
import { SCOPE_PROFILES_UI } from './scopeProfiles';
import type { ApiToken } from './useApiTokens';
import {
  useApiTokens,
  useCreateToken,
  useRotateToken,
  useRevokeToken,
} from './useApiTokens';
import { AdminTokensSection } from './AdminTokensSection';
import { EntityRefChips } from '../common/EntityRefChips';

/** Get group refs from ownershipEntityRefs (strips namespace generically). */
function getGroupRefs(ownershipEntityRefs: string[]): string[] {
  return ownershipEntityRefs.filter(ref => ref.startsWith('group:'));
}

/** Check membership of configurable admin groups (from app config, read on frontend). */
function checkIsAdmin(
  ownershipEntityRefs: string[],
  adminGroups: string[],
): boolean {
  const refSet = new Set(ownershipEntityRefs);
  return adminGroups.some(g => refSet.has(g));
}

export function ApiTokensSettingsTab() {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const adminGroups = configApi.getOptionalStringArray(
    'apiTokens.adminGroups',
  ) ?? ['group:default/backstage'];

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey(k => k + 1);

  const { value: identity } = useAsync(
    () => identityApi.getBackstageIdentity(),
    [identityApi],
  );
  const ownershipRefs = identity?.ownershipEntityRefs ?? [];
  const groupRefs = getGroupRefs(ownershipRefs);
  const isAdmin = checkIsAdmin(ownershipRefs, adminGroups);

  const { value: tokenList, loading, error } = useApiTokens(refreshKey);

  const [createOpen, setCreateOpen] = useState(false);
  const [oneTimeToken, setOneTimeToken] = useState<string | null>(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InfoCard
        title="API Tokens"
        subheader="Generate long-lived bearer tokens for programmatic access to the Backstage API. Tokens are shown once at creation — store them securely."
        action={
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setCreateOpen(true)}
            sx={{ mt: 1, mr: 1 }}
          >
            New token
          </Button>
        }
      >
        {loading && <CircularProgress size={24} />}

        {error && (
          <Typography variant="body2" color="error">
            {error.message}
          </Typography>
        )}

        {!loading && !error && tokenList?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tokens yet.
          </Typography>
        )}

        {!loading && !error && tokenList && tokenList.length > 0 && (
          <Stack gap={1.5}>
            {tokenList.map(token => (
              <TokenRow
                key={token.id}
                token={token}
                onRotated={newPlaintext => {
                  setOneTimeToken(newPlaintext);
                  refresh();
                }}
                onRevoked={refresh}
              />
            ))}
          </Stack>
        )}
      </InfoCard>

      {isAdmin && (
        <AdminTokensSection refreshKey={refreshKey} onRefresh={refresh} />
      )}

      {/* Create token dialog */}
      <CreateTokenDialog
        open={createOpen}
        groupRefs={groupRefs}
        onClose={() => setCreateOpen(false)}
        onCreated={plaintext => {
          setCreateOpen(false);
          setOneTimeToken(plaintext);
          refresh();
        }}
      />

      {/* One-time token reveal dialog */}
      <OneTimeTokenDialog
        token={oneTimeToken}
        onClose={() => setOneTimeToken(null)}
      />
    </Box>
  );
}

// ─── Token row ───────────────────────────────────────────────────────────────

function TokenRow({
  token,
  onRotated,
  onRevoked,
}: {
  token: ApiToken;
  onRotated: (plaintext: string) => void;
  onRevoked: () => void;
}) {
  const theme = useTheme();
  const themeTokens = getThemeTokens(theme);
  const { rotateToken, loading: rotating } = useRotateToken();
  const { revokeToken, loading: revoking } = useRevokeToken();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const handleRotate = async () => {
    const result = await rotateToken(token.id);
    onRotated(result.token);
  };

  const handleRevoke = async () => {
    await revokeToken(token.id);
    setConfirmRevoke(false);
    onRevoked();
  };

  const scopeLabels = token.scopes
    .map(s => SCOPE_PROFILES_UI.find(p => p.id === s)?.label ?? s)
    .join(', ');

  const formatDate = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: getSurfaceBorder(theme),
        backgroundColor: getSurfaceColor(theme, 'subtle'),
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
      >
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              variant="subtitle2"
              color={themeTokens.textPrimary}
              noWrap
            >
              {token.name}
            </Typography>
            {token.ownerType === 'team' && (
              <EntityRefChips refs={[token.subjectRef]} />
            )}
          </Stack>
          {token.description && (
            <Typography variant="body2" color={themeTokens.textMuted} mt={0.25}>
              {token.description}
            </Typography>
          )}
          <Stack direction="row" gap={2} mt={0.75} flexWrap="wrap">
            <Typography variant="caption" color={themeTokens.textMuted}>
              <strong>Prefix:</strong> {token.tokenPrefix}
            </Typography>
            <Typography variant="caption" color={themeTokens.textMuted}>
              <strong>Scopes:</strong> {scopeLabels}
            </Typography>
            <Typography variant="caption" color={themeTokens.textMuted}>
              <strong>Last used:</strong> {formatDate(token.lastUsedAt)}
            </Typography>
            {token.expiresAt && (
              <Typography variant="caption" color={themeTokens.textMuted}>
                <strong>Expires:</strong> {formatDate(token.expiresAt)}
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack direction="row" gap={0.5} flexShrink={0}>
          <Tooltip title="Rotate token (old token becomes invalid immediately)">
            <span>
              <IconButton
                size="small"
                onClick={handleRotate}
                disabled={rotating || revoking}
              >
                {rotating ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Revoke token">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirmRevoke(true)}
                disabled={rotating || revoking}
              >
                {revoking ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Revoke confirmation */}
      <Dialog
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revoke token?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{token.name}</strong> will be revoked immediately. Any
            services using it will lose access (note: there is a short
            propagation delay of up to ~30 seconds).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(false)}>Cancel</Button>
          <Button color="error" onClick={handleRevoke} disabled={revoking}>
            {revoking ? <CircularProgress size={16} /> : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────────

function CreateTokenDialog({
  open,
  groupRefs,
  onClose,
  onCreated,
}: {
  open: boolean;
  groupRefs: string[];
  onClose: () => void;
  onCreated: (plaintext: string) => void;
}) {
  const { createToken, loading } = useCreateToken();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerType, setOwnerType] = useState<'user' | 'team'>('user');
  const [subjectRef, setSubjectRef] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([
    'catalog:read',
  ]);
  // catalog:read vs catalog:readwrite are mutually exclusive; track separately
  const [catalogLevel, setCatalogLevel] = useState<
    'catalog:read' | 'catalog:readwrite'
  >('catalog:read');
  const [includesCatalog, setIncludesCatalog] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleClose = () => {
    setName('');
    setDescription('');
    setOwnerType('user');
    setSubjectRef('');
    setSelectedScopes(['catalog:read']);
    setCatalogLevel('catalog:read');
    setIncludesCatalog(true);
    setExpiresAt('');
    setFormError(null);
    onClose();
  };

  const computeScopes = (): string[] => {
    const scopes: string[] = [];
    if (includesCatalog) scopes.push(catalogLevel);
    for (const s of selectedScopes) {
      if (s !== 'catalog:read' && s !== 'catalog:readwrite') scopes.push(s);
    }
    return scopes;
  };

  const handleToggleScope = (scopeId: string) => {
    setSelectedScopes(prev =>
      prev.includes(scopeId)
        ? prev.filter(s => s !== scopeId)
        : [...prev, scopeId],
    );
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }
    const scopes = computeScopes();
    if (!scopes.length) {
      setFormError('Select at least one scope');
      return;
    }
    if (ownerType === 'team' && !subjectRef) {
      setFormError('Select a team');
      return;
    }

    try {
      const result = await createToken({
        name: name.trim(),
        description: description.trim() || undefined,
        ownerType,
        subjectRef: ownerType === 'team' ? subjectRef : undefined,
        scopes,
        expiresAt: expiresAt || undefined,
      });
      onCreated(result.token);
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  // Non-catalog scope profiles for the multi-select
  const otherProfiles = SCOPE_PROFILES_UI.filter(
    p => p.id !== 'catalog:read' && p.id !== 'catalog:readwrite',
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New API Token</DialogTitle>
      <DialogContent>
        <Stack gap={2.5} mt={0.5}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            size="small"
            required
            placeholder="e.g. ci-bot-catalog-reader"
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="What is this token for?"
          />

          {/* Owner */}
          <FormControl>
            <FormLabel>Owner</FormLabel>
            <RadioGroup
              value={ownerType}
              onChange={e => setOwnerType(e.target.value as 'user' | 'team')}
              row
            >
              <FormControlLabel
                value="user"
                control={<Radio size="small" />}
                label="Just me"
              />
              {groupRefs.length > 0 && (
                <FormControlLabel
                  value="team"
                  control={<Radio size="small" />}
                  label="A team I belong to"
                />
              )}
            </RadioGroup>
            {ownerType === 'team' && groupRefs.length > 0 && (
              <FormControl size="small" sx={{ mt: 1 }}>
                <Select
                  value={subjectRef}
                  onChange={e => setSubjectRef(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select team…
                  </MenuItem>
                  {groupRefs.map(ref => (
                    <MenuItem key={ref} value={ref}>
                      {ref.replace(/^group:[^/]+\//, '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </FormControl>

          {/* Scopes */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Scopes</FormLabel>
            <FormHelperText sx={{ mt: 0, mb: 1 }}>
              Select what this token is allowed to access.
            </FormHelperText>
            <FormGroup>
              {/* Catalog — special: read/readwrite are mutually exclusive */}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includesCatalog}
                    onChange={e => setIncludesCatalog(e.target.checked)}
                  />
                }
                label="Catalog"
              />
              {includesCatalog && (
                <Box ml={3.5} mb={0.5}>
                  <RadioGroup
                    value={catalogLevel}
                    onChange={e =>
                      setCatalogLevel(
                        e.target.value as 'catalog:read' | 'catalog:readwrite',
                      )
                    }
                    row
                  >
                    <FormControlLabel
                      value="catalog:read"
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">
                          Read-only (recommended)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="catalog:readwrite"
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">Read + write</Typography>
                      }
                    />
                  </RadioGroup>
                </Box>
              )}

              {/* Other scopes */}
              {otherProfiles.map(profile => (
                <FormControlLabel
                  key={profile.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedScopes.includes(profile.id)}
                      onChange={() => handleToggleScope(profile.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{profile.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {profile.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </FormControl>

          {/* Expiry */}
          <TextField
            label="Expires (optional)"
            type="date"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />

          {formError && (
            <Typography variant="body2" color="error">
              {formError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={16} /> : 'Create token'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── One-time token reveal dialog ─────────────────────────────────────────────

function OneTimeTokenDialog({
  token,
  onClose,
}: {
  token: string | null;
  onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!token) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Your new token</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          mb={2}
          color="warning.main"
          fontWeight={600}
        >
          ⚠ Copy this token now — it will not be shown again.
        </Typography>
        <TextField
          value={token}
          fullWidth
          size="small"
          type={visible ? 'text' : 'password'}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setVisible(v => !v)}>
                  {visible ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {copied && (
          <Typography
            variant="caption"
            color="success.main"
            mt={0.5}
            display="block"
          >
            Copied to clipboard
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
